<template>
  <div class="relative inline-block w-full">
    <label
      v-if="$slots.label && labeltop"
      :for="finalId"
      class="mb-1 block font-semibold"
    >
      <slot name="label" />
    </label>
    <template v-if="$slots.icon">
      <span class="absolute top-0 left-0 py-2 h-full flex items-center ml-3">
        <slot name="icon" />
      </span>
    </template>
    <template v-if="props.clearButton && selectedValue">
      <span
        class="absolute top-0 right-2 py-2 ml-3 h-full flex items-center z-40"
      >
        <button
          type="button"
          class="p-1 bg-neutral-50 rounded-lg cursor-pointer aspect-square"
          :aria-label="t('clear_aria_label')"
          @click="clearInput"
        >
          <UiIcon src="/svg/icons/cancel.svg" width="24" height="24" class="fill-neutral-800 rotate-180" aria-hidden="true" />
        </button>
      </span>
    </template>
    <input
      :id="finalId"
      v-model="selectedValue"
      :type="type"
      :name="name"
      :title="title"
      :value="selectedValue"
      :class="[
        {'pl-11 pr-6': $slots.icon && !props.clearButton},
        {'pl-11 pr-11': $slots.icon && props.clearButton},
        {'px-4': !$slots.icon && !xs},
        {'py-3 min-h-[51.59px]': normal && !$slots.label},
        {'py-2 ': slim && !$slots.label},
        {'py-1 px-1': xs && !labeltop && !$slots.icon},
        {'py-3 text-lg min-h-[55px] peer': normal && labeltop && $slots.label},
        {'py-3 text-lg peer': slim && labeltop && $slots.label},
        {'pt-6 pb-1 text-lg font-semibold min-h-[55px] peer': normal && labelinside && $slots.label},
        {'pt-3 pb-1 text-lg font-semibold peer': slim && labelinside && $slots.label},
        {'rounded-xl w-full': !props.inputstyle},
        {'bg-white': !props.bgColour},
        {'border-neutral-200': !error && !ruesSearchbar && !searchresultSearchbar},
        {'border-cta-700 bg-cta-50': error && !ruesSearchbar && !searchresultSearchbar},
        {'py-4 min-h-[51.59px]': (ruesSearchbar || searchresultSearchbar) && !$slots.label},
        {'lg:min-h-[46px]': searchresultSearchbar && !$slots.label},
        {'pt-6 pb-1 text-lg font-semibold min-h-[62px] peer w-full': (ruesSearchbar || searchresultSearchbar) && labelinside && $slots.label},
        {'lg:pt-4 lg:min-h-[46px]': searchresultSearchbar && labelinside && $slots.label},
        {'border text-left focus:outline-hidden focus:border-petrol-500 focus:ring-1 focus:ring-petrol-500 block' : !ruesSearchbar && !searchresultSearchbar},
        {'border-0 text-left focus:outline-hidden focus:bg-neutral-50 focus:ring-1 focus:ring-transparent block placeholder:text-neutral-900 rounded-t-xl lg:rounded-xl' : ruesSearchbar || searchresultSearchbar},
        props.inputstyle, props.bgColour,
        // Disabled: lighter gray background, not-allowed cursor, text stays legible.
        // '!bg' reliably overrides any set bgColour.
        {'!bg-neutral-50 cursor-not-allowed': props.disabled}
      ]"
      :placeholder="props.placeholder"
      :aria-label="props.accessibilityLabel || props.title || props.name"
      :autocomplete="autocompleteAttr"
      :disabled="props.disabled"
      @input="handleInput"
    >
    <label
      v-if="$slots.label && labelinside"
      :for="finalId"
      :class="[
        {'absolute -mt-3 ml-4': labelup && !ruesSearchbar},
        {'absolute text-lg text-petrol-700 font-semibold duration-200 transform -translate-y-3.5 scale-[75%] top-4 z-10 origin-left left-11 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1 peer-focus:scale-[75%] peer-focus:-translate-y-3.5': slim && !labelup},
        {'absolute text-lg text-petrol-700 font-semibold duration-200 transform -translate-y-2.5 scale-[85%] top-4 z-10 origin-left left-11 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-[85%] peer-focus:-translate-y-2.5': normal && !labelup},
        {'absolute text-lg text-neutral-900 duration-200 transform -translate-y-2.5 scale-[80%] z-10 origin-left left-11': ruesSearchbar || searchresultSearchbar},
        {'top-[1.15rem]': ruesSearchbar},
        {'top-[0.60rem]': searchresultSearchbar},
        'left-0'
      ]"
    >
      <slot name="label" />
    </label>
  </div>
</template>
<script setup lang="ts">
import type { DesignVariant } from '~/types/ui/DesignVariant'
import { isDesignVariant } from '~/types/ui/DesignVariant'

const props = defineProps({
  design: {
    type: String as PropType<DesignVariant>,
    default: 'normal',
    validator: (value: string) => isDesignVariant(value)
  },
  label: {
    type: String,
    default: ''
  },
  labelPosition: {
    type: String,
    default: 'inside'
  },
  accessibilityLabel: {
    type: String,
    default: ''
  },
  id: {
    type: String,
    default: null
  },
  inputstyle: {
    type: [String, Array],
    default: ''
  },
  bgColour: {
    type: String,
    default: 'bg-white'
  },
  placeholder: {
    type: String,
    default: ' '
  },
  name: {
    type: String,
    default: ''
  },
  type: {
    type: String,
    default: 'text'
  },
  title: {
    type: String,
    default: ''
  },
  modelValue: {
    type: [String, Number],
    default: ''
  },
  error: {
    type: Boolean,
    default: false
  },
  autocomplete: {
    // true → "on", false → "off", or a specific token like
    // "username" / "current-password" / "new-password" / "one-time-code"
    // (needed for browser/password-manager save & autofill).
    type: [Boolean, String],
    default: true
  },
  clearButton: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
})
const { translate: t } = useTranslations('UiInput')
const generatedId = useComponentId('input')
const finalId = computed(() => props.id ?? generatedId)

const normal = computed(() => props.design === 'normal')
const slim = computed(() => props.design === 'slim')
const xs = computed(() => props.design === 'xs')
const ruesSearchbar = computed(() => props.design === 'ruesSearchbar')
const searchresultSearchbar = computed(() => props.design === 'searchresultSearchbar')

// Label choices
const labelup = computed(() => props.label === 'up')
const labeltop = computed(() => props.labelPosition === 'top')
const labelinside = computed(() => props.labelPosition === 'inside')

const autocompleteAttr = computed(() => {
  if (typeof props.autocomplete === 'string') {
    return props.autocomplete
  }
  return props.autocomplete ? 'on' : 'off'
})

const emit = defineEmits<{(e: 'update:modelValue', value: string): void}>()

const selectedValue = ref(props.modelValue)

const handleInput = ({ target }: Event) => {
  emit('update:modelValue', (selectedValue.value = (target as HTMLInputElement).value))
}

const clearInput = () => {
  emit('update:modelValue', (selectedValue.value = ''))
}

watch(() => props.modelValue, (newValue) => {
  selectedValue.value = newValue
})

</script>
